'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  isChunkLoadError,
  recoverFromChunkLoadError
} from '#/lib/chunk-load-recovery';
import { reportClientError, toReport } from '#/lib/report-client-error';

export default function NlpTryError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // 청크 로드 실패는 오류 화면 대신 "다시 불러오는 중" 을 보여 준다 — 모바일 회선의
  // 일시 실패라 새로고침 한 번이면 대부분 회복된다. 입력은 보존·복원된다. (#273)
  const [reloading, setReloading] = useState(() => isChunkLoadError(error));

  useEffect(() => {
    console.error('[nlp-try/error]', error);

    if (isChunkLoadError(error)) {
      if (recoverFromChunkLoadError()) return; // 회복 중 — 챗에 알리지 않는다
      // 새로고침으로도 못 살아난 경우만 알린다. 스코프를 chunk-load 로 고정해
      // 어느 바운더리로 들어왔든 챗에서 같은 오류로 묶이게 한다.
      setReloading(false);
      reportClientError(toReport('chunk-load', error));
      return;
    }

    // 맞춤법·형태소 화면은 서비스의 본체다. 여기서 터지면 즉시 알아야 한다. (#262)
    reportClientError(toReport('nlp-try', error));
  }, [error]);

  if (reloading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
        <h1 className="text-lg font-semibold">
          페이지를 다시 불러오는 중입니다
        </h1>
        <p className="text-sm text-muted-foreground">
          연결이 잠시 불안정했습니다. 입력한 글은 그대로 유지됩니다.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
      <h1 className="text-lg font-semibold">일시적인 오류가 발생했습니다</h1>
      <p className="text-sm text-muted-foreground">
        서비스에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.
      </p>
      <div className="flex gap-3">
        <button
          onClick={() =>
            // 청크 오류는 리렌더(reset)가 아니라 새로고침이어야 새 청크를 다시 받아 온다.
            isChunkLoadError(error) ? window.location.reload() : reset()
          }
          className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
        >
          다시 시도
        </button>
        <Link
          href="/"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          홈으로
        </Link>
      </div>
    </div>
  );
}
